import { cheapestPrice, detailRows, eventLocation, firstImage } from "./format.js";
import type { PreviewFeed } from "./types.js";

export interface OteJsonEvent {
  id?: string;
  name?: string;
  startDate?: string;
  endDate?: string;
  timezone?: string;
  location?: { venue?: string; onlineUrl?: string };
  url?: string;
  description?: string;
  status?: string;
  attendanceMode?: "in-person" | "online" | "hybrid";
  languages?: string[];
  tags?: string[];
  updatedAt?: string;
  source?: unknown;
  image?: Array<string | { url: string; alt?: string }>;
  offers?: Array<{ name?: string; price?: number; currency?: string }>;
  organizers?: Array<{ name: string }>;
}

export interface OteJsonFeed {
  title?: string;
  description?: string;
  license?: string;
  events?: OteJsonEvent[];
}

export type OteJsonPreviewInput = OteJsonFeed | OteJsonEvent[];

export function oteJsonToPreviewFeed(input: OteJsonPreviewInput): PreviewFeed {
  const json = Array.isArray(input) ? { events: input } : input;
  if (!Array.isArray(json.events)) throw new Error("feed.json has no events array");
  return {
    title: json.title,
    description: json.description,
    license: json.license,
    events: json.events.map((event) => {
      const image = firstImage(event.image);
      const price = cheapestPrice(event.offers);
      const organizerName = event.organizers?.[0]?.name;
      return {
        name: event.name ?? "(untitled event)",
        startDate: event.startDate,
        endDate: event.endDate,
        timezone: event.timezone,
        location: eventLocation(event),
        locationLink: event.location?.onlineUrl,
        link: event.url ?? event.location?.onlineUrl,
        description: event.description,
        image,
        price,
        organizerName,
        tags: event.tags,
        attendanceMode: event.attendanceMode,
        updatedAt: event.updatedAt,
        details: detailRows([
          ["ID", event.id],
          ["Status", event.status],
          ["Timezone", event.timezone],
          ["Attendance", event.attendanceMode],
          ["Languages", event.languages],
          ["Tags", event.tags],
          ["Updated", event.updatedAt],
          ["Source", event.source],
          ["Image", image?.url],
          ["Price", price && `${price.amount}${price.currency ? ` ${price.currency}` : ""}`],
          ["Organizer", organizerName],
        ]),
      };
    }),
  };
}

export function jsonToPreviewFeed(text: string): PreviewFeed {
  return oteJsonToPreviewFeed(JSON.parse(text) as OteJsonPreviewInput);
}
